import React from 'react';
import { Box, Button, Chip, ChipOwnProps } from '@mui/material';
import { borderRadius, customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Benefits from './Benefits';

interface PricingPackageProps {
  packageName: string;
  description: string;
  price: string;
  duration: string;
  isLicenceIdCurrentUserPlan: boolean;
  packageHighlight: { enable: boolean, label?: string; color?: ChipOwnProps['color'], borderColor?: string };
  primaryCta: { label?: string; onClick?: () => void, href?: string };
  secondaryCta?: { label: string; onClick?: () => void, href?: string };
  benefits: { title: string; features: { text: string, isHighlighted: boolean, isBonus?: boolean }[] }[];
  isMostPopular?: boolean;
  caption?: string;
  showBenefitsSummary?: boolean;
}

const PricingPackage: React.FC<PricingPackageProps> = (props) => {
  const { t } = useTranslation();
  const {
    caption,
    packageName,
    description,
    price,
    duration,
    primaryCta,
    secondaryCta,
    benefits,
    isLicenceIdCurrentUserPlan,
    packageHighlight,
    showBenefitsSummary
  } = props;

  return (
    <Box
      data-testid='pricing-package'
      sx={{
        width: customSizes.pricingPackage.width,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.xl,
        border: `1px solid ${packageHighlight.enable ? packageHighlight.borderColor : palette.elevation.outlined}`,
        borderRadius: borderRadius.xl,
        backgroundColor: palette.common.white,
        position: 'relative',
        padding: spaces.lg,
      }}
    >
      {packageHighlight.enable && (
        <Chip 
          label={packageHighlight.label} 
          color={packageHighlight.color}
          size='medium'
          variant='filled'
          data-testid='most-popular-chip'
          sx={{ 
            height: 34, 
            position: 'absolute', 
            top: '-18px', // half of this Chip height + parent container padding
            left: '50%',
            transform: 'translateX(-50%)',
            '.MuiChip-label': {
              fontWeight: 500,
            },
          }}
        />
      )}
      <Box 
        data-testid='pricing-package-header'
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '80px' 
        }}
      >
        <Typography data-testid='pricing-package-caption' variant='caption' color={palette.text.primary}>
          {caption}
        </Typography>
        <Typography 
          data-testid='pricing-package-package-name' 
          variant='h4' 
          color={palette.text.primary}
          sx={{ fontWeight: 600 }}
        >
          {packageName}
        </Typography>
        <Typography data-testid='pricing-package-header-description' variant='body2' color={palette.text.secondary}>
          {description}
        </Typography>
      </Box>
      {!isLicenceIdCurrentUserPlan && (
        <>
          <Box data-testid='pricing-package-price'>
            <Typography
              data-testid='pricing-package-package-price'
              variant='h2'
              color={palette.text.primary}
              sx={{ fontWeight: 500 }}
            >
              {price}
            </Typography>
            <Typography 
              data-testid='pricing-package-duration' 
              variant='caption' 
              color={palette.text.secondary}
              sx={{ display: 'inline-block' }}
            >
              {duration}
            </Typography>
          </Box><Box
            data-testid='pricing-package-ctas'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.xs,
            }}
          >
            <Button
              data-testid='pricing-package-primary-cta'
              variant='contained'
              color='primary'
              size='large'
              sx={{ 
                width: '100%', 
                fontWeight: 500,
                '&:hover': {
                  color: palette.primary.contrastText,
                }
              }}
              onClick={primaryCta.onClick}
              role='link'
              href={primaryCta.href}
            >
              {primaryCta.label ? t(primaryCta.label) : t('Select package')}
            </Button>
            {secondaryCta && (
              <Button
                data-testid='pricing-package-secondary-cta'
                variant='text'
                color='primary'
                size='large'
                sx={{ 
                  width: '100%', 
                  fontWeight: 500,
                  '&:hover': {
                    color: palette.primary.main,
                  }
                }}
                onClick={secondaryCta.onClick}
                role='link'
                href={secondaryCta.href}
              >
                {secondaryCta.label}
              </Button>
            )}
          </Box>
        </>
      )}
      <Box data-testid='pricing-package-benefits'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xl,
        }}
      >
        {showBenefitsSummary && (
          <Typography variant='overline' color={palette.text.primary}>
            {t('Everything you currently have, plus:')}
          </Typography>
        )}
        {benefits.map((benefit, index) => <Benefits key={index} benefit={benefit} />)}
      </Box>
    </Box>
  );
};

export default PricingPackage;
